<template>
  <component
    :is="wrapper"
    :id="`collection-${collection}`"
    class="collection"
    :class="itemsClass"
  >
    <!-- Prepend -->
    <slot name="prepend" />

    <slot v-if="useFilterBar" name="filterbar">
      <!-- Filterbar Prepend -->
      <slot name="filterbar-prepend" />
      <!-- Filterbar -->
      <div :id="`filterbar-${collection}`" class="filterbar">
        <Component
          :is="filterBarComponent"
          :filters-id="filtersId"
          :sort="sort"
        />
      </div>
      <!-- Filterbar Append -->
      <slot name="filterbar-append" />
    </slot>

    <FkStrapiCollectionList
      :key="`${stateKey ? `${stateKey}-` : ``}${collection}-${filtersId}`"
      :filters-id="`${filtersId}`"
      :items-class="itemsClass"
      :collection="collection"
      :use-pagination="usePagination"
      :static-filters="filters"
      :populate="populate"
      :sort="sort"
      :items-wrapper="itemsWrapper"
      :item-wrapper="itemWrapper"
      :state-key="stateKey"
    >
      <!-- Custom item -->
      <template #customItem>
        <slot name="customItem" />
      </template>

      <!-- Loading -->
      <template #loading>
        <slot name="loading" />
      </template>

      <!-- error -->
      <template #error>
        <slot name="error" />
      </template>

      <!-- Items -->
      <template #items="{ data }">
        <slot name="items" v-bind="{ data }" />
      </template>

      <!-- empty -->
      <template #empty>
        <slot name="empty" />
      </template>

      <!-- pagination -->
      <template #pagination>
        <slot name="pagination" />
      </template>
    </FkStrapiCollectionList>

    <!-- Append -->
    <slot name="append" />
  </component>
</template>

<script setup lang="ts" generic="T extends StrapiAttributes">
import type { ConcreteComponent } from "vue";
import type { PaginationByPage } from "@nuxtjs/strapi";
import type { StrapiAttributes } from "../../../../../src/types";
import {
  useCollectionConfig,
  toRefs,
  watch,
  useId,
  resolveComponent,
} from "#imports";

import { useFilters } from "../../../composables/useFilters";

const { getCollectionConfig } = useCollectionConfig();
const filtersId = useId();

// Component properties
interface StrapiCollectionProps {
  itemsClass?: string[];
  collection: string;
  usePagination?: boolean;
  useFilterBar?: boolean;
  pagination?: PaginationByPage;
  filters?: Record<string, unknown>;
  populate?: Record<string, unknown>;
  sort?: string | string[];
  wrapper?: ConcreteComponent | string;
  itemsWrapper?: ConcreteComponent | string;
  itemWrapper?: ConcreteComponent | string;
  stateKey?: string;
}
const props = withDefaults(defineProps<StrapiCollectionProps>(), {
  itemsClass: () => [],
  usePagination: true,
  pagination: () => ({
    page: 1,
    pageSize: 10,
  }),
  populate: () => ({}),
  filters: () => ({}),
  useFilterBar: false,
  sort: undefined,
  wrapper: () => "div",
  itemsWrapper: () => "div",
  itemWrapper: () => resolveComponent("FkFragment"),
  stateKey: undefined,
});

//create refs from props
const { collection, pagination, sort } = toRefs(props);
watch(
  pagination,
  () => {
    const { pagination: currentPagination } = useFilters(`${filtersId}`);

    currentPagination.value = Object.assign(
      currentPagination.value,
      pagination.value,
    );
  },
  {
    immediate: true,
  },
);
//get collection config
const { filterBarComponent, populate } = getCollectionConfig(props.collection);
</script>
